<div class="dashboard-container">
  <app-header-actions
    [headerTitle]="'DASHBOARD.TITLE'"
    [headerIcon]="faChartBar"
    [config]="headerConfig"
    [showCount]="false"
    (backClick)="onBack()">
  </app-header-actions>

  <div class="filters-row">
    <div class="filters-left">
      <div class="filter-group scope-group">
        <label for="scope-filter">{{ 'DASHBOARD.FILTERS.SCOPE' | translate }}</label>
        <ng-select
          id="scope-filter"
          [items]="scopeOptions"
          bindLabel="name"
          bindValue="id"
          [searchable]="true"
          [clearable]="false"
          [loading]="usersLoading"
          [placeholder]="'DASHBOARD.SCOPE.SELECT_PLACEHOLDER' | translate"
          [(ngModel)]="selectedScopeOption"
          (ngModelChange)="onScopeSelectionChange($event)">
        </ng-select>
      </div>

      <div class="filter-group model-type-group">
        <label for="model-type-filter">{{ 'DASHBOARD.FILTERS.MODEL_TYPE' | translate }}</label>
        <ng-select
          id="model-type-filter"
          [items]="modelTypeOptions"
          bindLabel="name"
          bindValue="id"
          [searchable]="false"
          [clearable]="false"
          [(ngModel)]="selectedModelType"
          (ngModelChange)="onModelTypeSelectionChange($event)">
        </ng-select>
      </div>
    </div>

    <div class="filters-right">
      <div class="filter-group">
        <label for="start-date">{{ 'DASHBOARD.FILTERS.START_DATE' | translate }}</label>
        <input
          id="start-date"
          type="date"
          [min]="minDate"
          [max]="maxDate"
          [(ngModel)]="startDate">
      </div>

      <div class="filter-group">
        <label for="end-date">{{ 'DASHBOARD.FILTERS.END_DATE' | translate }}</label>
        <input
          id="end-date"
          type="date"
          [min]="minDate"
          [max]="maxDate"
          [(ngModel)]="endDate">
      </div>

      <div class="filter-group apply-group">
        <label>&nbsp;</label>
        <app-button
          [text]="'DASHBOARD.FILTERS.APPLY' | translate"
          [variant]="'primary'"
          [size]="'medium'"
          [fullWidth]="true"
          (clicked)="onApplyCustomPeriod()">
        </app-button>
      </div>
    </div>
  </div>

  @if (metrics?.scan?.truncated) {
    <div class="truncated-warning">
      <fa-icon [icon]="faExclamationTriangle"></fa-icon>
      <span>{{ 'DASHBOARD.TRUNCATED_WARNING' | translate:{ count: metrics?.scan?.scanned_results ?? 0, limit: metrics?.scan?.limit ?? 0 } }}</span>
    </div>
  }

  @if (metrics) {
    <div class="stats-grid">
      <div class="stat-card">
        <fa-icon [icon]="faDatabase" class="stat-icon"></fa-icon>
        <div class="stat-content">
          <span class="stat-value">{{ metrics.totals.results }}</span>
          <span class="stat-label">{{ 'DASHBOARD.METRICS.TOTAL_RESULTS' | translate }}</span>
        </div>
      </div>

      <div class="stat-card">
        <fa-icon [icon]="faUserCog" class="stat-icon"></fa-icon>
        <div class="stat-content">
          <span class="stat-value">{{ metrics.unique_interactions }}</span>
          <span class="stat-label">{{ 'DASHBOARD.METRICS.UNIQUE_INTERACTIONS' | translate }}</span>
        </div>
      </div>

      <div class="stat-card">
        <fa-icon [icon]="faCheckCircle" class="stat-icon"></fa-icon>
        <div class="stat-content">
          <span class="stat-value">{{ metrics.success_rate }}%</span>
          <span class="stat-label">{{ 'DASHBOARD.METRICS.SUCCESS_RATE' | translate }}</span>
        </div>
      </div>

      <div class="stat-card">
        <fa-icon [icon]="faClock" class="stat-icon"></fa-icon>
        <div class="stat-content">
          <span class="stat-value">{{ metrics.execution_time_s.avg_seconds }}s</span>
          <span class="stat-label">{{ 'DASHBOARD.METRICS.AVG_EXECUTION_TIME' | translate }}</span>
        </div>
      </div>
    </div>

    <div class="dashboard-sections">
      <section class="section-card">
        <h3>{{ 'DASHBOARD.SECTIONS.STATUS_BREAKDOWN' | translate }}</h3>
        <div class="status-chart">
          <canvas #statusChartCanvas></canvas>
          <div class="status-legend" #statusLegend></div>
        </div>
      </section>

      <section class="section-card">
        <h3>{{ 'DASHBOARD.SECTIONS.TOKEN_USAGE' | translate }}</h3>
        <div class="token-grid">
          <div class="token-item">
            <span class="token-label">{{ 'DASHBOARD.TOKENS.INPUT' | translate }}</span>
            <span class="token-value">{{ metrics.token_usage.input }}</span>
          </div>
          <div class="token-item">
            <span class="token-label">{{ 'DASHBOARD.TOKENS.OUTPUT' | translate }}</span>
            <span class="token-value">{{ metrics.token_usage.output }}</span>
          </div>
          <div class="token-item">
            <span class="token-label">{{ 'DASHBOARD.TOKENS.TOTAL' | translate }}</span>
            <span class="token-value">{{ metrics.token_usage.total }}</span>
          </div>
        </div>

        <div class="token-models">
          <div class="token-models-title">{{ 'DASHBOARD.TOKENS.TOP_MODELS' | translate }}</div>
          <div class="token-models-row token-models-header">
            <span>{{ 'DASHBOARD.TOKENS.MODEL' | translate }}</span>
            <span>{{ 'DASHBOARD.TOKENS.INPUT' | translate }}</span>
            <span>{{ 'DASHBOARD.TOKENS.OUTPUT' | translate }}</span>
            <span>{{ 'DASHBOARD.TOKENS.TOTAL' | translate }}</span>
          </div>
          @if (metrics.top_model_tokens.length === 0) {
            <div class="token-models-empty">-</div>
          } @else {
            @for (item of metrics.top_model_tokens; track item.model_id) {
              <div class="token-models-row">
                <span class="token-model-name" [title]="item.model_name">{{ item.model_name }}</span>
                <span>{{ item.input }}</span>
                <span>{{ item.output }}</span>
                <span>{{ item.total }}</span>
              </div>
            }
          }
        </div>
      </section>

      <section class="section-card">
        <h3>{{ 'DASHBOARD.SECTIONS.TOP_CONFIGURATIONS' | translate }}</h3>

        <div class="config-groups">
          <div class="config-group">
            <h4>{{ 'DASHBOARD.CONFIG_TYPES.DELFOS_SERVER' | translate }}</h4>
            <ul>
              @for (item of getConfigUsage('delfos_server_config_id'); track item.config_id) {
                <li>
                  <span>{{ item.config_key || item.config_id }}</span>
                  <strong>{{ item.count }} ({{ item.percentage }}%)</strong>
                </li>
              }
            </ul>
          </div>

          <div class="config-group">
            <h4>{{ 'DASHBOARD.CONFIG_TYPES.DELFOS_LLM' | translate }}</h4>
            <ul>
              @for (item of getConfigUsage('delfos_llm_config_id'); track item.config_id) {
                <li>
                  <span>{{ item.config_key || item.config_id }}</span>
                  <strong>{{ item.count }} ({{ item.percentage }}%)</strong>
                </li>
              }
            </ul>
          </div>

          <div class="config-group">
            <h4>{{ 'DASHBOARD.CONFIG_TYPES.DI2WINOCR_SERVER' | translate }}</h4>
            <ul>
              @for (item of getConfigUsage('di2winocr_server_config_id'); track item.config_id) {
                <li>
                  <span>{{ item.config_key || item.config_id }}</span>
                  <strong>{{ item.count }} ({{ item.percentage }}%)</strong>
                </li>
              }
            </ul>
          </div>

          <div class="config-group">
            <h4>{{ 'DASHBOARD.CONFIG_TYPES.DI2WINOCR_FEATURES' | translate }}</h4>
            <ul>
              @for (item of getConfigUsage('di2winocr_features_config_id'); track item.config_id) {
                <li>
                  <span>{{ item.config_key || item.config_id }}</span>
                  <strong>{{ item.count }} ({{ item.percentage }}%)</strong>
                </li>
              }
            </ul>
          </div>
        </div>
      </section>

      <section class="section-card">
        <div class="top-interactions-header">
          <h3>{{ 'DASHBOARD.SECTIONS.TOP_INTERACTIONS' | translate }}</h3>
          <input
            type="text"
            class="top-interactions-search"
            [value]="topSearchTerm"
            [placeholder]="'DASHBOARD.TOP_INTERACTIONS.SEARCH_PLACEHOLDER' | translate"
            (input)="onTopSearchChange($any($event.target).value)">
        </div>
        @if (metrics.top_interactions_by_tokens.length === 0) {
          <p class="empty-text">{{ 'DASHBOARD.EMPTY.TOP_INTERACTIONS' | translate }}</p>
        } @else {
          <ul class="top-interactions-list">
            @for (interaction of metrics.top_interactions_by_tokens; track interaction.interaction_id) {
              <li>
                <div class="interaction-left">
                  <span class="rank">#{{ interaction.rank }}</span>
                  <span class="name">{{ interaction.interaction_name || interaction.interaction_id }}</span>
                </div>
                <div class="interaction-right">
                  @if (metrics.window.scope === 'all') {
                    <span class="user" [title]="interaction.user_email || interaction.user_name || ''">{{ interaction.user_name || interaction.user_email || '-' }}</span>
                  }
                  <strong>{{ interaction.total_tokens }}</strong>
                </div>
              </li>
            }
          </ul>

          <app-pagination
            [currentPage]="metrics.top_interactions_page"
            [pageSize]="metrics.top_interactions_page_size"
            [totalItems]="metrics.top_interactions_total"
            [totalPages]="metrics.top_interactions_total_pages"
            (pageChange)="onTopPageChange($any($event))">
          </app-pagination>
        }
      </section>

      <section class="section-card">
        <h3>{{ 'DASHBOARD.SECTIONS.MODELS_OVER_TIME' | translate }}</h3>
        <div class="models-series-chart">
          <canvas #modelsSeriesChartCanvas></canvas>
        </div>
      </section>

      <section class="section-card">
        <h3>{{ 'DASHBOARD.SECTIONS.FILE_TYPES' | translate }}</h3>
        <div class="file-types-chart">
          <canvas #fileTypesChartCanvas></canvas>
        </div>
      </section>
    </div>
  }

  @if (loading) {
    <div class="loading-overlay">
      {{ 'MESSAGE.LOADING' | translate }}
    </div>
  }
</div>
